import { useMutation, useQueryClient } from '@tanstack/react-query';

import { _cancelGammaTeamInvitation, _deleteGammaTeamMember } from '@/api/gamma-team/mutations';
import { GAMMA_TEAM_QUERY_KEYS } from '@/api/gamma-team/types';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { Modal } from '@/shared/design-system';

import { CONFIRM_MODAL_MAX_WIDTH } from './confirm-modal.constants';

export const DELETE_GAMMA_MEMBER_TITLE =
  'Are you sure you want to remove this team member from Gamma Prime?';
export const DELETE_GAMMA_MEMBER_BODY =
  'Once removed, the user will need to be invited again to rejoin.';
export const CANCEL_GAMMA_INVITATION_TITLE = 'Are you sure you want to cancel this invitation?';
export const CANCEL_GAMMA_INVITATION_BODY = 'The person can be invited again later.';

export type GammaTeamMemberDeleteProps = {
  userId: string | null;
  invitationId: string | null;
  isSuperAdmin?: boolean;
};

export const GammaTeamMemberDelete = () => {
  const { open, handleClose, props } = useGlobalModal('GAMMA_TEAM_MEMBER_DELETE_MODAL');
  const queryClient = useQueryClient();
  const { mutate: deleteMember } = useMutation({ mutationFn: _deleteGammaTeamMember });
  const { mutate: cancelInvitation } = useMutation({ mutationFn: _cancelGammaTeamInvitation });
  const isInvitation = !!props?.invitationId;

  const handleOnSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [GAMMA_TEAM_QUERY_KEYS.dashboard],
    });
    handleClose();
  };

  const handleDelete = () => {
    if (props?.invitationId) {
      cancelInvitation(props.invitationId, { onSuccess: handleOnSuccess });
    } else if (props?.userId) {
      deleteMember(props.userId, { onSuccess: handleOnSuccess });
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => !isOpen && handleClose()}
      showClose
      maxWidth={CONFIRM_MODAL_MAX_WIDTH}
      title={isInvitation ? CANCEL_GAMMA_INVITATION_TITLE : DELETE_GAMMA_MEMBER_TITLE}
      description={isInvitation ? CANCEL_GAMMA_INVITATION_BODY : DELETE_GAMMA_MEMBER_BODY}
      primaryAction={{
        label: isInvitation ? 'Cancel invitation' : 'Delete',
        color: 'destructive',
        onClick: handleDelete,
      }}
      secondaryAction={{ label: 'Cancel', onClick: handleClose }}
    >
      <></>
    </Modal>
  );
};
